import { useContext, useEffect, useState, Suspense } from "react";
import { MeshRefractionMaterial, useGLTF } from "@react-three/drei";
import { useLoader } from "@react-three/fiber";
import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader.js";
import * as THREE from "three";
import stateContext from "../../Context/StateContext";

export const CharmStone = () => {
    const globalState = useContext(stateContext);
    const [modelUrl, setModelUrl] = useState(null);
    const [diamondKey, setDiamondKey] = useState("");

    // Load HDR environment texture
    const texture = useLoader(
        RGBELoader,
        `${process.env.PUBLIC_URL}/assets/textures/hdri/ROOM-F - 1.1.hdr`
        // `${process.env.PUBLIC_URL}/assets/textures/hdri/empty_warehouse_01_1k.hdr`
    );

    // Update model URL when selectedCharmData changes
    useEffect(() => {
        if (globalState.selectedCharmData?.glbFileKey) {
            const url = `${process.env.REACT_APP_FILE_BASE_URL}${globalState.selectedCharmData.glbFileKey}`;
            setModelUrl(url);
        } else {
            setModelUrl(`${process.env.PUBLIC_URL}/assets/glbs/dummy.glb`);
        }
    }, [globalState.selectedCharmData]);

    // Always call useGLTF unconditionally
    const { nodes } = useGLTF(
        modelUrl || `${process.env.PUBLIC_URL}/assets/glbs/dummy.glb`
    );

    // Find the diamond mesh key
    useEffect(() => {
        if (!nodes) return;
        const key = Object.keys(nodes).find(
            (k) => nodes[k].isMesh && nodes[k].name.includes("diamond")
        );
        if (key) setDiamondKey(key);
    }, [nodes]);

    return (
        <Suspense fallback={null}>
            <group ref={globalState.charmRef}>
                {Object.entries(nodes).map(([key, node]) => {
                    if (!node.isMesh || key === diamondKey) return null;

                    // Default scale & position
                    let scale = node.scale;
                    let position = new THREE.Vector3(
                        node.position.x,
                        node.position.y,
                        node.position.z - globalState.fontThickness / 2
                    );

                    // Override based on mesh name
                    if (node.name === "socket") {
                        scale = new THREE.Vector3(
                            node.scale.x,
                            node.scale.y,
                            globalState.fontThickness === 1.02 ? 6.55 : 7.65
                        );
                    }

                    if (node.name === "socket1") {
                        scale = new THREE.Vector3(
                            node.scale.x,
                            node.scale.y,
                            globalState.fontThickness === 1.02 ? 2.2 : 3.55
                        );
                        position = new THREE.Vector3(
                            node.position.x,
                            node.position.y,
                            globalState.fontThickness === 1.02
                                ? globalState.fontThickness * 1.94
                                : globalState.fontThickness * 2.87
                        );
                    }

                    return (
                        <mesh
                            key={key}
                            geometry={node.geometry}
                            position={position}
                            rotation={node.rotation}
                            scale={scale}
                            castShadow
                            receiveShadow
                            material={globalState.globalMaterial.default}
                        />
                    );
                })}

                {/* Render diamond mesh with MeshRefractionMaterial */}
                {diamondKey && nodes[diamondKey] && (
                    <mesh
                        key={diamondKey}
                        geometry={nodes[diamondKey].geometry}
                        position={[
                            nodes[diamondKey].position.x,
                            nodes[diamondKey].position.y,
                            nodes[diamondKey].position.z - 0.3,
                        ]}
                        rotation={nodes[diamondKey].rotation}
                        scale={nodes[diamondKey].scale}
                        castShadow
                        receiveShadow
                    >
                        <MeshRefractionMaterial
                            envMap={texture}
                            color={globalState.charmColor}
                        />
                    </mesh>
                )}
            </group>
        </Suspense>
    );
};

// Preload default GLB
useGLTF.preload(`${process.env.PUBLIC_URL}/assets/glbs/dummy.glb`);
